import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { FaPlus } from 'react-icons/fa';

import { apiService } from '../../components/Config/api.service';
import EmployeeSelect from '../../components/Formulaire/EmployeeSelect';
import HeaderCard from '../../components/Header/HeaderCard';
import TableList from '../../components/Tools/TableList';
import LoadingBarWithStyles from '../../components/Utils/LoadingBarWithStyles';
import { BOUTON_PRINCIPAL, enveloppeDe } from '../Employee/organigrammeOutils';
import { CLASSE_CHAMP, EtatDepart } from './departOutils';

/**
 * Les départs de la société (contrat C34, Yros/Kavero#62) : la liste, et le
 * formulaire « Nouveau départ » qui crée le départ en brouillon puis ouvre sa
 * fiche. Réservé au gestionnaire RH (`canGererDeparts`).
 *
 * Patron d'`OrdresMission.js` : un seul appel, une recherche locale sur le nom
 * du salarié, le salarié choisi avec `EmployeeSelect`. Le portail ne juge que
 * la présence des champs ; un salarié d'une autre société, des dates
 * incohérentes ou un départ déjà en cours, c'est Odoo qui refuse, et son
 * message s'affiche mot pour mot.
 */
const FORMULAIRE_VIDE = { salarie: null, motifId: '', dateRupture: '', joursPreavis: '', dateEntree: '' };

function Departs() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [departs, setDeparts] = useState([]);
  const [motifs, setMotifs] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState(null);
  const [recherche, setRecherche] = useState('');
  const [formulaire, setFormulaire] = useState(FORMULAIRE_VIDE);
  const [ouvert, setOuvert] = useState(false);
  const [envoi, setEnvoi] = useState(false);
  const [erreurFormulaire, setErreurFormulaire] = useState('');

  const charger = useCallback(async () => {
    setChargement(true);
    setErreur(null);
    const [liste, listeMotifs] = await Promise.all([
      enveloppeDe(() => apiService.listerDeparts()),
      enveloppeDe(() => apiService.motifsDepart()),
    ]);
    if (liste.ok) {
      setDeparts(liste.departs || []);
    } else {
      setDeparts([]);
      setErreur(liste.message || 'generique');
    }
    setMotifs(listeMotifs.ok ? listeMotifs.motifs || [] : []);
    setChargement(false);
  }, []);

  useEffect(() => {
    charger();
  }, [charger]);

  const changer = (champ, valeur) => setFormulaire((courant) => ({ ...courant, [champ]: valeur }));

  const creer = async (event) => {
    event.preventDefault();
    const { salarie, motifId, dateRupture, joursPreavis, dateEntree } = formulaire;
    if (!salarie || !motifId || !dateRupture) {
      setErreurFormulaire(t('departs.champsManquants'));
      return;
    }
    const depart = { employe_id: salarie.value, motif_id: Number(motifId), date_rupture: dateRupture };
    if (joursPreavis !== '') depart.jours_preavis = Number(joursPreavis);
    if (dateEntree) depart.date_entree = dateEntree;

    setEnvoi(true);
    setErreurFormulaire('');
    const reponse = await enveloppeDe(() => apiService.creerDepart(depart));
    setEnvoi(false);
    if (reponse.ok) {
      navigate(`/rh/departs/${reponse.depart.id}`);
    } else {
      setErreurFormulaire(reponse.message || t('departs.erreurGenerique'));
    }
  };

  const departsAffiches = useMemo(() => {
    // `setSearchQuery` de TableList rend un tableau de termes : seul le premier
    // compte, comme dans OrdresMission.js.
    const brut = Array.isArray(recherche) ? recherche[0] : recherche;
    const terme = (brut || '').trim().toLowerCase();
    return departs.filter((depart) => !terme || (depart.employe?.nom || '').toLowerCase().includes(terme));
  }, [departs, recherche]);

  const colonnes = useMemo(
    () => [
      {
        key: 'salarie',
        label: t('departs.colSalarie'),
        render: (depart) => <span data-testid={`depart-ligne-${depart.id}`}>{depart.employe?.nom || '—'}</span>,
      },
      { key: 'motif', label: t('departs.colMotif'), render: (depart) => depart.motif?.nom || '—' },
      { key: 'rupture', label: t('departs.colRupture'), render: (depart) => depart.date_rupture },
      { key: 'etat', label: t('departs.colEtat'), render: (depart) => <EtatDepart etat={depart.etat} /> },
    ],
    [t]
  );

  return (
    <div className="p-4 space-y-4">
      <HeaderCard title={t('departs.titre')} text={t('departs.description')} />

      <div className="bg-white p-4 sm:p-6 rounded-2xl shadow-sm border border-slate-100">
        {!ouvert ? (
          <button type="button" data-testid="depart-nouveau" className={BOUTON_PRINCIPAL} onClick={() => setOuvert(true)}>
            <FaPlus size={12} /> {t('departs.nouveau')}
          </button>
        ) : (
          <form data-testid="depart-formulaire" onSubmit={creer} className="space-y-4">
            <h2 className="text-base sm:text-lg font-semibold">{t('departs.nouveau')}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="text-sm font-medium">
                <span>{t('departs.salarie')}</span>
                <div className="mt-1">
                  <EmployeeSelect value={formulaire.salarie} onChange={(option) => changer('salarie', option)} />
                </div>
              </div>
              <label className="block text-sm font-medium">
                {t('departs.motif')}
                <select data-testid="depart-motif" value={formulaire.motifId}
                  onChange={(e) => changer('motifId', e.target.value)} className={CLASSE_CHAMP}>
                  <option value="">{t('departs.choisirMotif')}</option>
                  {motifs.map((motif) => <option key={motif.id} value={motif.id}>{motif.nom}</option>)}
                </select>
              </label>
              <label className="block text-sm font-medium">
                {t('departs.dateRupture')}
                <input type="date" data-testid="depart-date-rupture" value={formulaire.dateRupture}
                  onChange={(e) => changer('dateRupture', e.target.value)} className={CLASSE_CHAMP} />
              </label>
              <label className="block text-sm font-medium">
                {t('departs.joursPreavis')}
                <input type="number" min="0" data-testid="depart-preavis" value={formulaire.joursPreavis}
                  onChange={(e) => changer('joursPreavis', e.target.value)} className={CLASSE_CHAMP} />
              </label>
              <label className="block text-sm font-medium">
                {t('departs.dateEntree')}
                <input type="date" data-testid="depart-date-entree" value={formulaire.dateEntree}
                  onChange={(e) => changer('dateEntree', e.target.value)} className={CLASSE_CHAMP} />
                <span className="block mt-1 text-xs font-normal text-gray-500">{t('departs.dateEntreeAide')}</span>
              </label>
            </div>
            {erreurFormulaire && (
              <p role="alert" data-testid="depart-erreur-formulaire"
                className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-3">
                {erreurFormulaire}
              </p>
            )}
            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
              <button type="button" className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg"
                onClick={() => { setOuvert(false); setFormulaire(FORMULAIRE_VIDE); setErreurFormulaire(''); }}>
                {t('departs.annuler')}
              </button>
              <button type="submit" data-testid="depart-creer" disabled={envoi} className={BOUTON_PRINCIPAL}>
                {envoi ? t('departs.envoi') : t('departs.creer')}
              </button>
            </div>
          </form>
        )}
      </div>

      {erreur && (
        <p data-testid="departs-erreur" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-3">
          {erreur === 'generique' ? t('departs.erreurGenerique') : erreur}
        </p>
      )}

      {chargement ? (
        <LoadingBarWithStyles />
      ) : (
        !erreur && (
          <TableList
            columns={colonnes}
            data={departsAffiches}
            searchQuery={recherche}
            setSearchQuery={setRecherche}
            showFilter={false}
            onRowClick={(depart) => navigate(`/rh/departs/${depart.id}`)}
          />
        )
      )}
    </div>
  );
}

export default Departs;
